import { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShoppingBag, Globe, CheckCircle2, FileText, Send, Package, ShieldCheck, Loader2 } from "lucide-react";
import PSQuoteCalculator from "./PSQuoteCalculator";

const CATEGORIES = [
  { emoji: "👗", key: "fashion" },
  { emoji: "💄", key: "beauty" },
  { emoji: "🍼", key: "baby" },
  { emoji: "📱", key: "electronics" },
  { emoji: "🛋️", key: "home" },
  { emoji: "⚽", key: "sport" },
  { emoji: "🔧", key: "auto" },
  { emoji: "🧴", key: "cleaning" },
  { emoji: "🥘", key: "food" },
];

const STEPS = [
  { n: 1, key: "send_refs" },
  { n: 2, key: "receive_quote" },
  { n: 3, key: "we_buy" },
  { n: 4, key: "we_ship" },
  { n: 5, key: "delivered" },
];

const COMMITMENTS = [
  { key: "trusted_brands" },
  { key: "transparent" },
  { key: "quote_first" },
  { key: "tracking" },
  { key: "personal_support" },
];

export default function PersonalShopper() {
  const { t } = useLang();
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", delivery_country: "",
    product_link: "", description: "", quantity: 1, budget: "", comments: "", photo_url: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      set("photo_url", `${backendUrl}${data.url}`);
      toast.success(t("ps.photo_added"));
    } catch { toast.error(t("ps.photo_fail")); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.delivery_country || !form.description) {
      toast.error(t("ps.required_fields"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/personal-shopper/request", { ...form, quantity: parseInt(form.quantity) || 1 });
      setDone(true);
      toast.success(t("ps.submitted"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="ps-thanks">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-9 h-9 text-green-600"/>
        </div>
        <h1 className="heading font-extrabold text-3xl text-[#111812]">{t("ps.thanks_title")}</h1>
        <p className="text-[#4B5563] mt-3">{t("ps.thanks_desc")}</p>
        <Button onClick={() => { setDone(false); setForm({ full_name:"", email:"", phone:"", delivery_country:"", product_link:"", description:"", quantity:1, budget:"", comments:"", photo_url:"" }); }} className="mt-6 pill-btn bg-[#1A3322] rounded-full" data-testid="ps-new-request">{t("ps.new_request")}</Button>
      </div>
    );
  }

  return (
    <div data-testid="personal-shopper-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1A3322] via-[#24472F] to-[#C85A32] text-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 sm:py-20 relative z-10">
          <span className="inline-flex items-center gap-2 bg-white/15 border border-white/20 backdrop-blur px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider" data-testid="ps-eyebrow">
            <ShoppingBag className="w-3.5 h-3.5"/>{t("ps.eyebrow")}
          </span>
          <h1 className="heading font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-5 leading-tight" data-testid="ps-title">
            {t("ps.title_line1")}<br/>
            <span className="text-[#D98A2C]">{t("ps.title_line2")}</span>
          </h1>
          <p className="text-white/85 mt-5 max-w-2xl text-base sm:text-lg leading-relaxed">{t("ps.subtitle")}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#ps-calculator">
              <Button className="pill-btn bg-white text-[#1A3322] hover:bg-[#F5F5F0] rounded-full h-12 px-7 font-bold" data-testid="ps-cta-hero">
                <Send className="w-4 h-4 mr-2"/>{t("ps.cta_start")}
              </Button>
            </a>
            <a href="#ps-how">
              <Button variant="outline" className="rounded-full h-12 px-6 border-white/40 bg-transparent text-white hover:bg-white hover:text-[#1A3322]" data-testid="ps-cta-learn">
                {t("ps.cta_how")}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Quote calculator (Devis + Paramètres tabs) */}
      <PSQuoteCalculator/>

      {/* How it works */}
      <section id="ps-how" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="label-uppercase text-xs text-[#C85A32]">{t("ps.how_eyebrow")}</span>
          <h2 className="heading font-extrabold text-3xl sm:text-4xl text-[#111812] mt-2">{t("ps.how_title")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {STEPS.map((s) => (
            <div key={s.n} className="text-center" data-testid={`ps-step-${s.n}`}>
              <div className="w-14 h-14 rounded-full bg-[#1A3322] text-white heading font-extrabold text-xl flex items-center justify-center mx-auto mb-3">{s.n}</div>
              <p className="text-sm font-semibold text-[#111812]">{t(`ps.step.${s.key}.title`)}</p>
              <p className="text-xs text-[#4B5563] mt-1 leading-relaxed">{t(`ps.step.${s.key}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Categories we buy */}
      <section className="bg-[#F5F5F0] py-16">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="label-uppercase text-xs text-[#C85A32]">{t("ps.where_eyebrow")}</span>
            <h2 className="heading font-extrabold text-3xl sm:text-4xl text-[#111812] mt-2">{t("ps.where_title")}</h2>
            <p className="text-[#4B5563] mt-3">{t("ps.where_desc")}</p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-3">
            {CATEGORIES.map((c) => (
              <div key={c.key} className="bg-white rounded-xl p-4 text-center hover:shadow-md transition" data-testid={`ps-cat-${c.key}`}>
                <div className="text-3xl mb-1">{c.emoji}</div>
                <p className="text-[11px] font-semibold text-[#111812] leading-tight">{t(`ps.cat.${c.key}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="label-uppercase text-xs text-[#C85A32]">{t("ps.commit_eyebrow")}</span>
          <h2 className="heading font-extrabold text-3xl sm:text-4xl text-[#111812] mt-2">{t("ps.commit_title")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {COMMITMENTS.map((c) => (
            <div key={c.key} className="bg-white border border-[#1A3322]/10 rounded-lg p-4 flex items-start gap-2.5" data-testid={`ps-commit-${c.key}`}>
              <ShieldCheck className="w-5 h-5 text-[#16a34a] shrink-0 mt-0.5"/>
              <p className="text-sm text-[#111812] font-medium">{t(`ps.commit.${c.key}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing info */}
      <section className="bg-gradient-to-br from-[#1A3322] to-[#24472F] text-white py-12">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-90"/>
          <h2 className="heading font-extrabold text-2xl sm:text-3xl">{t("ps.pricing_title")}</h2>
          <p className="text-white/80 mt-3 leading-relaxed">{t("ps.pricing_desc")}</p>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-2xl mx-auto">
            {["products", "service_fee", "packaging", "shipping"].map((k) => (
              <div key={k} className="bg-white/10 rounded-lg p-3 text-sm text-white/95" data-testid={`ps-fee-${k}`}>
                <Package className="w-4 h-4 mx-auto mb-1 text-[#D98A2C]"/>
                {t(`ps.fee.${k}`)}
              </div>
            ))}
          </div>
          <p className="text-white/70 text-xs mt-5">{t("ps.pricing_footnote")}</p>
        </div>
      </section>

      {/* FORM */}
      <section id="ps-form" className="max-w-[900px] mx-auto px-6 lg:px-10 py-16">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="label-uppercase text-xs text-[#C85A32]">{t("ps.form_eyebrow")}</span>
          <h2 className="heading font-extrabold text-3xl sm:text-4xl text-[#111812] mt-2">{t("ps.form_title")}</h2>
          <p className="text-[#4B5563] mt-3">{t("ps.form_subtitle")}</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#1A3322]/10 rounded-2xl p-6 sm:p-8 space-y-4 shadow-sm" data-testid="ps-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("ps.f.name")} *</Label>
              <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} required data-testid="ps-input-name" placeholder="Marie Mbayo"/>
            </div>
            <div className="space-y-1.5">
              <Label>{t("ps.f.email")} *</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="ps-input-email" placeholder="marie@example.com"/>
            </div>
            <div className="space-y-1.5">
              <Label>{t("ps.f.phone")}</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ps-input-phone" placeholder="+243 812 345 678"/>
            </div>
            <div className="space-y-1.5">
              <Label>{t("ps.f.country")} *</Label>
              <Input value={form.delivery_country} onChange={(e) => set("delivery_country", e.target.value)} required data-testid="ps-input-country" placeholder="France, RDC, Belgique…"/>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("ps.f.link")}</Label>
            <Input value={form.product_link} onChange={(e) => set("product_link", e.target.value)} data-testid="ps-input-link" placeholder="https://www.zara.com/… ou nom du magasin"/>
            <p className="text-[11px] text-[#4B5563]">{t("ps.f.link_hint")}</p>
          </div>

          <div className="space-y-1.5">
            <Label>{t("ps.f.description")} *</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} required data-testid="ps-input-description" placeholder={t("ps.f.description_placeholder")}/>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>{t("ps.f.quantity")} *</Label>
              <Input type="number" min="1" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required data-testid="ps-input-quantity"/>
            </div>
            <div className="space-y-1.5">
              <Label>{t("ps.f.budget")}</Label>
              <Input value={form.budget} onChange={(e) => set("budget", e.target.value)} data-testid="ps-input-budget" placeholder="≈ 150 EUR"/>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("ps.f.photo")}</Label>
            <Input type="file" accept="image/*" onChange={uploadPhoto} data-testid="ps-input-photo"/>
            {uploading && <p className="text-xs text-[#4B5563] flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin"/>{t("ps.uploading")}</p>}
            {form.photo_url && !uploading && (
              <div className="flex items-center gap-2 text-xs text-green-700">
                <CheckCircle2 className="w-4 h-4"/><span>{t("ps.photo_added")}</span>
                <img src={form.photo_url} alt="" className="w-10 h-10 rounded object-cover ml-2"/>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t("ps.f.comments")}</Label>
            <Textarea rows={3} value={form.comments} onChange={(e) => set("comments", e.target.value)} data-testid="ps-input-comments" placeholder={t("ps.f.comments_placeholder")}/>
          </div>

          <div className="pt-3">
            <Button type="submit" disabled={busy} className="w-full pill-btn bg-[#C85A32] hover:bg-[#D98A2C] rounded-full h-12 text-base font-bold" data-testid="ps-submit-btn">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>{t("common.loading")}</> : <><Send className="w-4 h-4 mr-2"/>{t("ps.submit")}</>}
            </Button>
            <p className="text-[11px] text-[#4B5563] text-center mt-3">{t("ps.submit_hint")}</p>
          </div>
        </form>
      </section>
    </div>
  );
}
